<template>
  <b-container fluid>
    <admin-preloader :show="loading"/>
    <admin-breadcrumb :items="[{ text: 'Taxonomies', path: {name: 'admin-taxonomies'} }]" name="Non-METRC Taxonomy" no-title/>

    <b-alert show variant="info" class="py-2">
      Archived categories are excluded from product assignment, marketplace filters, search, and normal category resources.
    </b-alert>

    <admin-panel title="Non-METRC Taxonomy">
      <template v-slot:header-right>
        <b-form-checkbox v-model="includeArchived" switch class="mr-3 mt-1">
          Include archived
        </b-form-checkbox>
        <b-btn variant="outline-secondary" class="mr-2" :disabled="loading" @click="loadAll">
          Refresh
        </b-btn>
        <no-permission-wrapper v-if="activeTabIndex === 0" :show="!canCreateTaxonomies">
          <b-btn variant="success" :disabled="!canCreateTaxonomies" @click="openCategoryModal()">Add Category</b-btn>
        </no-permission-wrapper>
        <no-permission-wrapper v-if="activeTabIndex === 1" :show="!canCreateTaxonomies">
          <b-btn variant="success" :disabled="!canCreateTaxonomies" @click="openFacetValueModal()">Add Facet Value</b-btn>
        </no-permission-wrapper>
        <no-permission-wrapper v-if="activeTabIndex === 2" :show="!canCreateTaxonomies">
          <b-btn variant="success" :disabled="!canCreateTaxonomies" @click="openBrowseGroupModal()">Add Browse Group</b-btn>
        </no-permission-wrapper>
      </template>

      <b-tabs v-model="activeTabIndex" content-class="pt-3">
        <b-tab title="Categories">
          <b-table
              :fields="categoryFields"
              :items="filteredCategories"
              :busy="loading"
              table-class="text-nowrap"
              responsive
              show-empty
              empty-text="No non-METRC categories found."
          >
            <template #cell(name)="row">
              <div class="non-metrc-taxonomy__description-cell">
                <div class="font-weight-bold">{{ row.item.name }}</div>
                <small class="text-muted">{{ row.item.definition || 'No definition' }}</small>
              </div>
            </template>
            <template #cell(state)="row">
              <b-badge :variant="row.item.is_archived ? 'secondary' : 'success'">
                {{ row.item.is_archived ? 'Archived' : 'Active' }}
              </b-badge>
            </template>
            <template #cell(groups)="row">
              <span v-if="countRelated(row.item.browse_groups, row.item.browse_group_ids)">{{ labels(row.item.browse_groups, 'display_name') }}</span>
              <span v-else class="text-muted">None</span>
            </template>
            <template #cell(facet_count)="row">
              {{ countRelated(row.item.facet_values, row.item.facet_value_ids) }}
            </template>
            <template #cell(product_assignment_count)="row">
              {{ categoryProductCount(row.item) }}
            </template>
            <template #cell(actions)="row">
              <no-permission-wrapper :show="!canUpdateTaxonomies">
                <b-btn size="sm" variant="primary" class="mr-1" :disabled="!canUpdateTaxonomies" @click="openCategoryModal(row.item)">Edit</b-btn>
              </no-permission-wrapper>
              <no-permission-wrapper v-if="!row.item.is_archived" :show="!canDeleteTaxonomies">
                <b-btn size="sm" variant="danger" :disabled="!canDeleteTaxonomies" @click="confirmArchive('category', row.item)">Archive</b-btn>
              </no-permission-wrapper>
              <no-permission-wrapper v-else :show="!canUpdateTaxonomies">
                <b-btn size="sm" variant="success" :disabled="!canUpdateTaxonomies" @click="restore('category', row.item)">Restore</b-btn>
              </no-permission-wrapper>
            </template>
          </b-table>
        </b-tab>

        <b-tab title="Facet Values">
          <b-table
              :fields="facetValueFields"
              :items="filteredFacetValues"
              :busy="loading"
              table-class="text-nowrap"
              responsive
              show-empty
              empty-text="No non-METRC facet values found."
          >
            <template #cell(facet_key)="row">
              {{ facetDimensionLabel(row.item.facet_key) }}
            </template>
            <template #cell(label)="row">
              <div class="font-weight-bold">{{ row.item.label }}</div>
              <small class="text-muted">{{ row.item.facet_key }}</small>
            </template>
            <template #cell(state)="row">
              <b-badge :variant="row.item.is_archived ? 'secondary' : 'success'">
                {{ row.item.is_archived ? 'Archived' : 'Active' }}
              </b-badge>
            </template>
            <template #cell(category_count)="row">
              {{ countIds(row.item.category_ids) }}
            </template>
            <template #cell(actions)="row">
              <no-permission-wrapper :show="!canUpdateTaxonomies">
                <b-btn size="sm" variant="primary" class="mr-1" :disabled="!canUpdateTaxonomies" @click="openFacetValueModal(row.item)">Edit</b-btn>
              </no-permission-wrapper>
              <no-permission-wrapper v-if="!row.item.is_archived" :show="!canDeleteTaxonomies">
                <b-btn size="sm" variant="danger" :disabled="!canDeleteTaxonomies" @click="confirmArchive('facet_value', row.item)">Archive</b-btn>
              </no-permission-wrapper>
              <no-permission-wrapper v-else :show="!canUpdateTaxonomies">
                <b-btn size="sm" variant="success" :disabled="!canUpdateTaxonomies" @click="restore('facet_value', row.item)">Restore</b-btn>
              </no-permission-wrapper>
            </template>
          </b-table>
        </b-tab>

        <b-tab title="Browse Groups">
          <b-table
              :fields="browseGroupFields"
              :items="filteredBrowseGroups"
              :busy="loading"
              table-class="text-nowrap"
              responsive
              show-empty
              empty-text="No non-METRC browse groups found."
          >
            <template #cell(display_name)="row">
              <div class="non-metrc-taxonomy__description-cell">
                <div class="font-weight-bold">{{ row.item.display_name }}</div>
                <small class="text-muted">{{ row.item.description || 'No description' }}</small>
              </div>
            </template>
            <template #cell(state)="row">
              <b-badge :variant="row.item.is_archived ? 'secondary' : 'success'">
                {{ row.item.is_archived ? 'Archived' : 'Active' }}
              </b-badge>
            </template>
            <template #cell(category_count)="row">
              {{ countIds(row.item.category_ids) }}
            </template>
            <template #cell(actions)="row">
              <no-permission-wrapper :show="!canUpdateTaxonomies">
                <b-btn size="sm" variant="primary" class="mr-1" :disabled="!canUpdateTaxonomies" @click="openBrowseGroupModal(row.item)">Edit</b-btn>
              </no-permission-wrapper>
              <no-permission-wrapper v-if="!row.item.is_archived" :show="!canDeleteTaxonomies">
                <b-btn size="sm" variant="danger" :disabled="!canDeleteTaxonomies" @click="confirmArchive('browse_group', row.item)">Archive</b-btn>
              </no-permission-wrapper>
              <no-permission-wrapper v-else :show="!canUpdateTaxonomies">
                <b-btn size="sm" variant="success" :disabled="!canUpdateTaxonomies" @click="restore('browse_group', row.item)">Restore</b-btn>
              </no-permission-wrapper>
            </template>
          </b-table>
        </b-tab>

        <b-tab title="Archived Assignments">
          <b-table
              :fields="archivedAssignmentFields"
              :items="archivedCategories"
              :busy="loading"
              table-class="text-nowrap"
              responsive
              show-empty
              :empty-text="includeArchived ? 'No archived category assignments found.' : 'Enable Include archived to load archived assignments.'"
          >
            <template #cell(name)="row">
              <div class="font-weight-bold">{{ row.item.name }}</div>
              <small class="text-muted">{{ row.item.code }}</small>
            </template>
            <template #cell(archived_at)="row">
              {{ formatDate(row.item.archived_at) }}
            </template>
            <template #cell(facet_assignments)="row">
              {{ countRelated(row.item.facet_values, row.item.facet_value_ids) }}
            </template>
            <template #cell(browse_group_assignments)="row">
              {{ countRelated(row.item.browse_groups, row.item.browse_group_ids) }}
            </template>
            <template #cell(product_assignments)="row">
              {{ categoryProductCount(row.item) }}
            </template>
            <template #cell(actions)="row">
              <no-permission-wrapper :show="!canUpdateTaxonomies">
                <b-btn size="sm" variant="success" :disabled="!canUpdateTaxonomies" @click="restore('category', row.item)">Restore</b-btn>
              </no-permission-wrapper>
            </template>
          </b-table>
        </b-tab>
      </b-tabs>
    </admin-panel>

    <admin-non-metrc-category-modal
        v-if="categoryModal.visible"
        :visible="categoryModal.visible"
        :category="categoryModal.record"
        :facet-values="facetValues"
        :browse-groups="browseGroups"
        :facet-dimensions="facetDimensions"
        @cancel="closeCategoryModal"
        @success="onCategorySaved"
    />
    <admin-non-metrc-facet-value-modal
        v-if="facetValueModal.visible"
        :visible="facetValueModal.visible"
        :facet-value="facetValueModal.record"
        :facet-dimensions="facetDimensions"
        @cancel="closeFacetValueModal"
        @success="onFacetValueSaved"
    />
    <admin-non-metrc-browse-group-modal
        v-if="browseGroupModal.visible"
        :visible="browseGroupModal.visible"
        :browse-group="browseGroupModal.record"
        @cancel="closeBrowseGroupModal"
        @success="onBrowseGroupSaved"
    />
  </b-container>
</template>

<script>
import AdminPanel from "../components/AdminPanel";
import AdminPreloader from "../components/AdminPreloader";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import NoPermissionWrapper from "../components/NoPermissionWrapper.vue";
import AdminNonMetrcCategoryModal from "../components/AdminNonMetrcCategoryModal.vue";
import AdminNonMetrcFacetValueModal from "../components/AdminNonMetrcFacetValueModal.vue";
import AdminNonMetrcBrowseGroupModal from "../components/AdminNonMetrcBrowseGroupModal.vue";
import {API_ROUTES} from "../api";

const FACET_DIMENSIONS = [
  {value: 'market_context', text: 'Market Context'},
  {value: 'operator_context', text: 'Operator Context'},
  {value: 'facility_context', text: 'Facility Context'},
];

export default {
  name: "AdminNonMetrcTaxonomy",
  components: {
    AdminNonMetrcBrowseGroupModal,
    AdminNonMetrcFacetValueModal,
    AdminNonMetrcCategoryModal,
    NoPermissionWrapper,
    AdminBreadcrumb,
    AdminPreloader,
    AdminPanel
  },
  data() {
    return {
      loading: false,
      includeArchived: false,
      activeTabIndex: 0,
      categories: [],
      facetValues: [],
      browseGroups: [],
      categoryModal: {
        visible: false,
        record: null
      },
      facetValueModal: {
        visible: false,
        record: null
      },
      browseGroupModal: {
        visible: false,
        record: null
      },
      archiveImpacts: {
        category: {},
        facet_value: {},
        browse_group: {}
      },
      categoryFields: [
        {key: 'name', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'code', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'state', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'groups', label: 'Groups', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'facet_count', label: 'Facets', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'product_assignment_count', label: 'Products', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'actions', thClass: 'border-top-0', tdClass: 'align-middle text-right'}
      ],
      facetValueFields: [
        {key: 'facet_key', label: 'Dimension', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'label', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'value_key', label: 'Value Key', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'state', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'category_count', label: 'Categories', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'actions', thClass: 'border-top-0', tdClass: 'align-middle text-right'}
      ],
      browseGroupFields: [
        {key: 'display_name', label: 'Name', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'group_key', label: 'Group Key', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'state', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'category_count', label: 'Categories', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'actions', thClass: 'border-top-0', tdClass: 'align-middle text-right'}
      ],
      archivedAssignmentFields: [
        {key: 'name', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'archived_at', label: 'Archived At', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'facet_assignments', label: 'Facet Assignments', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'browse_group_assignments', label: 'Browse Group Assignments', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'product_assignments', label: 'Products', thClass: 'border-top-0', tdClass: 'align-middle'},
        {key: 'actions', thClass: 'border-top-0', tdClass: 'align-middle text-right'}
      ]
    }
  },
  computed: {
    canCreateTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_create');
    },
    canUpdateTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_update');
    },
    canDeleteTaxonomies() {
      return this.$store.getters['auth/hasAdminPermissions']('taxonomy_delete');
    },
    search() {
      return this.$store.state.current.search;
    },
    facetDimensions() {
      return FACET_DIMENSIONS;
    },
    filteredCategories() {
      return this.filterRows(this.categories, item => [
        item.name,
        item.code,
        item.definition,
        item.admin_notes,
        this.labels(item.browse_groups, 'display_name'),
        this.labels(item.facet_values, 'label')
      ]);
    },
    filteredFacetValues() {
      return this.filterRows(this.facetValues, item => [
        item.facet_key,
        this.facetDimensionLabel(item.facet_key),
        item.value_key,
        item.label
      ]);
    },
    filteredBrowseGroups() {
      return this.filterRows(this.browseGroups, item => [
        item.strategy_key,
        item.group_key,
        item.display_name,
        item.description
      ]);
    },
    archivedCategories() {
      return this.filteredCategories.filter(category => category.is_archived);
    }
  },
  watch: {
    includeArchived() {
      this.loadAll();
    }
  },
  created() {
    this.loadAll();
  },
  methods: {
    loadAll() {
      this.loading = true;
      let options = {
        params: this.includeArchived ? {include_archived: 1} : {}
      };

      Promise.all([
        this.$api.get(API_ROUTES.ADMIN_NON_METRC_TAXONOMY_CATEGORIES, options),
        this.$api.get(API_ROUTES.ADMIN_NON_METRC_TAXONOMY_FACET_VALUES, options),
        this.$api.get(API_ROUTES.ADMIN_NON_METRC_TAXONOMY_BROWSE_GROUPS, options),
      ]).then(([categories, facetValues, browseGroups]) => {
        this.categories = categories.data.categories ?? [];
        this.facetValues = facetValues.data.facet_values ?? [];
        this.browseGroups = browseGroups.data.browse_groups ?? [];
      }).catch(error => {
        this.$bvToast.toast(error.response?.data?.message ?? 'Failed to load non-METRC taxonomy data', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    filterRows(rows, fieldsCallback) {
      let rowsToFilter = this.includeArchived ? rows : rows.filter(row => !row.is_archived);
      if (!this.search) {
        return rowsToFilter;
      }

      let search = this.search.toLowerCase();
      return rowsToFilter.filter(row => fieldsCallback(row).join(' ').toLowerCase().indexOf(search) >= 0);
    },
    checkPermission(permission) {
      if (!this[permission]) {
        this.$bvToast.toast('Sorry! You don\'t have permission to do this action', {
          title: 'Denied!',
          variant: 'danger'
        });
        return false;
      }
      return true;
    },
    openCategoryModal(category = null) {
      if (!this.checkPermission(category ? 'canUpdateTaxonomies' : 'canCreateTaxonomies')) {
        return;
      }
      this.categoryModal = {
        visible: true,
        record: category
      };
    },
    closeCategoryModal() {
      this.categoryModal = {
        visible: false,
        record: null
      };
    },
    openFacetValueModal(facetValue = null) {
      if (!this.checkPermission(facetValue ? 'canUpdateTaxonomies' : 'canCreateTaxonomies')) {
        return;
      }
      this.facetValueModal = {
        visible: true,
        record: facetValue
      };
    },
    closeFacetValueModal() {
      this.facetValueModal = {
        visible: false,
        record: null
      };
    },
    openBrowseGroupModal(browseGroup = null) {
      if (!this.checkPermission(browseGroup ? 'canUpdateTaxonomies' : 'canCreateTaxonomies')) {
        return;
      }
      this.browseGroupModal = {
        visible: true,
        record: browseGroup
      };
    },
    closeBrowseGroupModal() {
      this.browseGroupModal = {
        visible: false,
        record: null
      };
    },
    onCategorySaved(category) {
      this.upsert('categories', category);
      this.closeCategoryModal();
      this.$bvToast.toast('Category has been successfully saved', {
        title: 'Success!',
        variant: 'success'
      });
    },
    onFacetValueSaved(facetValue) {
      this.upsert('facetValues', facetValue);
      this.closeFacetValueModal();
      this.$bvToast.toast('Facet value has been successfully saved', {
        title: 'Success!',
        variant: 'success'
      });
    },
    onBrowseGroupSaved(browseGroup) {
      this.upsert('browseGroups', browseGroup);
      this.closeBrowseGroupModal();
      this.$bvToast.toast('Browse group has been successfully saved', {
        title: 'Success!',
        variant: 'success'
      });
    },
    upsert(listName, item) {
      let rows = [...this[listName]];
      let index = rows.findIndex(row => row.id === item.id);
      if (index >= 0) {
        rows[index] = item;
      } else {
        rows.push(item);
      }
      this[listName] = rows;
    },
    removeFromList(listName, id) {
      this[listName] = this[listName].filter(row => row.id !== id);
    },
    confirmArchive(type, item) {
      if (!this.checkPermission('canDeleteTaxonomies')) {
        return;
      }

      this.$bvModal.msgBoxConfirm(this.archiveConfirmMessage(type, item), {
        title: 'Please confirm',
        okVariant: 'danger',
        okTitle: 'Archive',
        centered: true
      }).then(confirmed => {
        if (confirmed) {
          this.archive(type, item);
        }
      });
    },
    archive(type, item) {
      this.loading = true;
      this.$api.post(`${this.routeFor(type)}/${item.id}/archive`).then(({data}) => {
        let archivedItem = this.itemFromArchiveResponse(type, data);
        this.$set(this.archiveImpacts[type], item.id, data.impact ?? null);

        if (this.includeArchived) {
          this.upsert(this.listNameFor(type), archivedItem);
        } else {
          this.removeFromList(this.listNameFor(type), item.id);
        }

        this.$bvToast.toast(this.archiveToastMessage(data.impact), {
          title: 'Archived',
          variant: 'success'
        });
      }).catch(error => {
        this.$bvToast.toast(error.response?.data?.message ?? 'Failed to archive record', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    restore(type, item) {
      if (!this.checkPermission('canUpdateTaxonomies')) {
        return;
      }

      this.loading = true;
      this.$api.post(`${this.routeFor(type)}/${item.id}/restore`).then(({data}) => {
        this.upsert(this.listNameFor(type), this.itemFromRestoreResponse(type, data));
        this.$bvToast.toast('Record has been successfully restored', {
          title: 'Restored',
          variant: 'success'
        });
      }).catch(error => {
        this.$bvToast.toast(error.response?.data?.message ?? 'Failed to restore record', {
          title: error.response?.status === 409 ? 'Conflict' : 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.loading = false;
      });
    },
    routeFor(type) {
      if (type === 'category') {
        return API_ROUTES.ADMIN_NON_METRC_TAXONOMY_CATEGORIES;
      }
      if (type === 'facet_value') {
        return API_ROUTES.ADMIN_NON_METRC_TAXONOMY_FACET_VALUES;
      }
      return API_ROUTES.ADMIN_NON_METRC_TAXONOMY_BROWSE_GROUPS;
    },
    listNameFor(type) {
      if (type === 'category') {
        return 'categories';
      }
      if (type === 'facet_value') {
        return 'facetValues';
      }
      return 'browseGroups';
    },
    itemFromArchiveResponse(type, data) {
      if (type === 'category') {
        return data.category;
      }
      if (type === 'facet_value') {
        return data.facet_value;
      }
      return data.browse_group;
    },
    itemFromRestoreResponse(type, data) {
      if (type === 'category') {
        return data.category;
      }
      if (type === 'facet_value') {
        return data.facet_value;
      }
      return data.browse_group;
    },
    archiveConfirmMessage(type, item) {
      if (type === 'category') {
        return [
          `Archive ${item.name}?`,
          `Products assigned: ${this.categoryProductCount(item)}`,
          `Category-facet assignments affected: ${this.countRelated(item.facet_values, item.facet_value_ids)}`,
          `Category-browse-group assignments affected: ${this.countRelated(item.browse_groups, item.browse_group_ids)}`,
          'Exact buyer filter disappearance counts are returned by the archive response.'
        ].join('\n');
      }

      if (type === 'facet_value') {
        return [
          `Archive ${item.label}?`,
          `Active category assignments affected: ${this.countIds(item.category_ids)}`
        ].join('\n');
      }

      return [
        `Archive ${item.display_name}?`,
        `Active category assignments affected: ${this.countIds(item.category_ids)}`
      ].join('\n');
    },
    archiveToastMessage(impact) {
      if (!impact?.before) {
        return 'Record has been successfully archived';
      }

      let before = impact.before;
      if (typeof before.products_assigned !== 'undefined') {
        return `Record archived. Products assigned: ${before.products_assigned}; facets affected: ${before.category_facet_assignments}; browse groups affected: ${before.category_browse_group_assignments}.`;
      }

      if (typeof before.active_category_assignments !== 'undefined') {
        return `Record archived. Active category assignments affected: ${before.active_category_assignments}.`;
      }

      return 'Record has been successfully archived';
    },
    categoryProductCount(category) {
      let impact = this.archiveImpacts.category[category.id]?.before;
      let count = category.products_assigned
          ?? category.active_products_assigned
          ?? category.product_assignment_count
          ?? category.products_count
          ?? impact?.products_assigned;

      return typeof count === 'undefined' ? 'Not returned' : count;
    },
    countRelated(items, ids) {
      if (Array.isArray(items)) {
        return items.length;
      }
      return this.countIds(ids);
    },
    countIds(ids) {
      return Array.isArray(ids) ? ids.length : 0;
    },
    labels(items, field) {
      if (!Array.isArray(items) || items.length === 0) {
        return '';
      }
      return items.map(item => `${item[field]}${item.is_archived ? ' (Archived)' : ''}`).join(', ');
    },
    facetDimensionLabel(facetKey) {
      return this.facetDimensions.find(dimension => dimension.value === facetKey)?.text ?? facetKey;
    },
    formatDate(value) {
      if (!value) {
        return 'N/A';
      }
      return new Date(value).toLocaleString();
    }
  }
}
</script>

<style scoped>
.non-metrc-taxonomy__description-cell {
  max-width: 760px;
  min-width: 280px;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.non-metrc-taxonomy__description-cell small {
  display: block;
}
</style>
